import { codes } from 'country-calling-code';

export const INDONESIA_ISO = 'ID';

/** Strip spaces, dots, dashes, and parentheses from pasted phone numbers. */
export function sanitizePhoneInput(str) {
  return String(str).replace(/[\s.\-()]/g, '');
}

export function normalizeCountryCode(code) {
  return sanitizePhoneInput(code);
}

export function buildCountryOptions() {
  const sorted = [...codes].sort((a, b) => a.country.localeCompare(b.country));
  const idIndex = sorted.findIndex((c) => c.isoCode2 === INDONESIA_ISO);
  if (idIndex > 0) {
    const [indonesia] = sorted.splice(idIndex, 1);
    sorted.unshift(indonesia);
  }
  return sorted;
}

export function getCountryByIso(isoCode2, options = buildCountryOptions()) {
  return options.find((c) => c.isoCode2 === isoCode2);
}

/** Match country name, ISO codes, or dialing code (with or without +). */
export function filterCountryOptions(options, { inputValue }) {
  const query = inputValue.trim().toLowerCase().replace(/^\+/, '');
  if (!query) {
    return options;
  }

  return options.filter((country) => {
    const code = normalizeCountryCode(country.countryCodes[0]);
    const haystack = `${country.country} ${country.isoCode2} ${country.isoCode3} ${code} +${code}`.toLowerCase();
    return haystack.includes(query);
  });
}

/** Regional-indicator flag emoji from ISO 3166-1 alpha-2 (e.g. ID → 🇮🇩). */
export function isoToFlagEmoji(isoCode2) {
  if (!isoCode2 || isoCode2.length !== 2) {
    return '';
  }
  return [...isoCode2.toUpperCase()]
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('');
}

export function formatCountryCodeLabel(country) {
  if (!country) {
    return '';
  }
  const code = normalizeCountryCode(country.countryCodes[0]);
  return `+${code}`;
}

export function formatCountryAriaLabel(country) {
  return `${country.country}, ${formatCountryCodeLabel(country)}`;
}

/**
 * Build E.164-style digits for WhatsApp from local input and selected country code.
 */
export function buildWhatsAppPhoneNumber(localInput, countryCodeDigits) {
  let local = sanitizePhoneInput(localInput);

  if (!local) {
    return { ok: false, error: 'Field is empty' };
  }

  if (local.startsWith('+')) {
    local = local.slice(1);
  }

  const code = normalizeCountryCode(countryCodeDigits);

  if (local.startsWith(code)) {
    local = local.slice(code.length);
  }

  if (local.startsWith('0')) {
    local = local.slice(1);
  }

  if (!local) {
    return { ok: false, error: 'Please enter a valid phone number' };
  }

  if (!/^\d+$/.test(local)) {
    return {
      ok: false,
      error: 'Contains invalid characters. Please enter numbers only',
    };
  }

  const phone = code + local;

  if (!/^\d+$/.test(phone)) {
    return {
      ok: false,
      error: 'Contains invalid characters. Please enter numbers only',
    };
  }

  return { ok: true, phone };
}
